import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, RefreshCw } from "lucide-react";
import { ContentEnginePanel } from "@/components/admin/ContentEnginePanel";

interface Metrics {
  since: string;
  visitors: number;
  returning_visitors: number;
  total_events: number;
  events: { event: string; count: number; visitors: number }[];
  top_paths: { path: string; count: number; visitors: number }[];
  devices: { device: string; visitors: number }[];
  daily: { day: string; visitors: number; returning_events: number }[];
}

const RANGES = [7, 30, 90];

const AdminMetrics = () => {
  const { user, loading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Metrics | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data: roleData, error: roleError } = await supabase.rpc("has_role", {
        _user_id: user.id,
        _role: "admin",
      });
      if (!cancelled) setIsAdmin(!roleError && roleData === true);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    const { data: result, error: rpcError } = await supabase.rpc(
      "admin_product_metrics" as any,
      { _days: days } as any,
    );
    if (rpcError) setError(rpcError.message);
    else setData(result as unknown as Metrics);
    setBusy(false);
  }, [days]);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  if (loading || (user && isAdmin === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;

  const repeatRate =
    data && data.visitors > 0
      ? Math.round((data.returning_visitors / data.visitors) * 100)
      : 0;

  const eventCount = (name: string) =>
    data?.events.find((e) => e.event === name)?.visitors ?? 0;

  const waitViewers = eventCount("wait_view");
  const pct = (n: number) =>
    waitViewers > 0 ? `${Math.round((n / waitViewers) * 100)}%` : "—";

  return (
    <div className="min-h-screen bg-background px-4 py-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Usage</h1>
            <p className="text-base text-muted-foreground">
              Anonymous, based on this site only.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {RANGES.map((r) => (
              <Button
                key={r}
                variant={days === r ? "default" : "outline"}
                onClick={() => setDays(r)}
                className="min-h-[44px] rounded-xl active:scale-95"
              >
                {r}d
              </Button>
            ))}
            <Button
              variant="outline"
              onClick={() => void load()}
              disabled={busy}
              className="min-h-[44px] rounded-xl active:scale-95"
              aria-label="Refresh"
            >
              <RefreshCw className={busy ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
            </Button>
          </div>
        </div>

        {error && (
          <Card className="rounded-xl shadow-md border-destructive/40">
            <CardContent className="py-4 text-base text-destructive">{error}</CardContent>
          </Card>
        )}

        {busy && !data ? (
          <div className="py-16 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : data ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="People" value={data.visitors.toLocaleString()} />
              <Stat
                label="Came back"
                value={`${repeatRate}%`}
                hint={`${data.returning_visitors.toLocaleString()} people`}
              />
              <Stat label="Actions" value={data.total_events.toLocaleString()} />
              <Stat
                label="Checked a wait"
                value={waitViewers.toLocaleString()}
              />
            </div>

            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-3">
                <h2 className="text-xl font-bold text-foreground">
                  What happens after someone checks a wait time
                </h2>
                <FunnelRow label="Checked a wait time" value={waitViewers} pct="100%" />
                <FunnelRow
                  label="Worked out when to leave"
                  value={eventCount("leave_by_used")}
                  pct={pct(eventCount("leave_by_used"))}
                />
                <FunnelRow
                  label="Tapped a Get the app button"
                  value={eventCount("app_cta_click")}
                  pct={pct(eventCount("app_cta_click"))}
                />
                <FunnelRow
                  label="Started creating an account"
                  value={eventCount("signup_start")}
                  pct={pct(eventCount("signup_start"))}
                />
                <FunnelRow
                  label="Created an account"
                  value={eventCount("signup_complete")}
                  pct={pct(eventCount("signup_complete"))}
                />
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-4">
              <ListCard
                title="Busiest pages"
                rows={data.top_paths.map((p) => ({
                  label: p.path,
                  value: `${p.visitors.toLocaleString()} people`,
                }))}
              />
              <ListCard
                title="Every action recorded"
                rows={data.events.map((e) => ({
                  label: e.event.replace(/_/g, " "),
                  value: `${e.count.toLocaleString()}`,
                }))}
              />
            </div>

            <ListCard
              title="Day by day"
              rows={data.daily.map((d) => ({
                label: d.day,
                value: `${d.visitors.toLocaleString()} people`,
              }))}
            />
          </>
        ) : (
          <Card className="rounded-xl shadow-md">
            <CardContent className="py-6 text-base text-muted-foreground">
              Nothing recorded yet. Numbers start appearing as people use the site.
            </CardContent>
          </Card>
        )}

        <ContentEnginePanel />
      </div>
    </div>
  );
};

const Stat = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <Card className="rounded-xl shadow-md">
    <CardContent className="py-5">
      <p className="text-base text-muted-foreground">{label}</p>
      <p className="text-3xl font-bold text-foreground mt-1">{value}</p>
      {hint && <p className="text-sm text-muted-foreground mt-1">{hint}</p>}
    </CardContent>
  </Card>
);

const FunnelRow = ({ label, value, pct }: { label: string; value: number; pct: string }) => (
  <div className="flex items-center justify-between gap-4 border-b border-border/50 pb-2 last:border-0">
    <span className="text-base text-foreground">{label}</span>
    <span className="text-base text-muted-foreground whitespace-nowrap">
      {value.toLocaleString()} · {pct}
    </span>
  </div>
);

const ListCard = ({
  title,
  rows,
}: {
  title: string;
  rows: { label: string; value: string }[];
}) => (
  <Card className="rounded-xl shadow-md">
    <CardContent className="py-6 space-y-2">
      <h2 className="text-xl font-bold text-foreground mb-2">{title}</h2>
      {rows.length === 0 ? (
        <p className="text-base text-muted-foreground">Nothing yet.</p>
      ) : (
        rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between gap-4 text-base">
            <span className="text-muted-foreground truncate">{r.label}</span>
            <span className="text-foreground whitespace-nowrap">{r.value}</span>
          </div>
        ))
      )}
    </CardContent>
  </Card>
);

export default AdminMetrics;
